/**
 * Consumíveis de Líquido Penetrante — cadastro global de END.
 *
 * Cada produto usado no ensaio (penetrante, removedor/limpador, revelador)
 * é registrado uma vez, com marca, produto, lote e validade, e depois apenas
 * SELECIONADO dentro do relatório LP. O relatório copia o texto no momento da
 * seleção, para que uma edição posterior do cadastro nunca reescreva um
 * documento já emitido. Dado mestre da empresa inspetora — sem escopo de
 * cliente, como os procedimentos e os catálogos técnicos.
 */

export type LpConsumableType = "PENETRANT" | "REMOVER" | "DEVELOPER";

export const LP_CONSUMABLE_TYPES: readonly LpConsumableType[] = ["PENETRANT", "REMOVER", "DEVELOPER"] as const;

export const LP_CONSUMABLE_TYPE_LABELS: Record<LpConsumableType, string> = {
  PENETRANT: "Penetrante",
  REMOVER: "Removedor / Limpador",
  DEVELOPER: "Revelador"
};

export type LpConsumableStatus = "ACTIVE" | "INACTIVE";

export interface LpConsumable {
  id: string;
  type: LpConsumableType;
  brand: string;
  product: string;
  lot: string;
  /** "yyyy-mm-dd" — data civil, comparada como texto (nunca via new Date()). */
  validity: string;
  status: LpConsumableStatus;
  observations: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

/** Mensagens por campo — nunca um "dados inválidos" genérico. */
export function validateLpConsumable(input: Partial<LpConsumable>): string[] {
  const errors: string[] = [];
  if (!input.type || !LP_CONSUMABLE_TYPES.includes(input.type as LpConsumableType)) {
    errors.push("Informe o Tipo do consumível (Penetrante, Removedor ou Revelador).");
  }
  if (!input.brand || !input.brand.trim()) errors.push("Informe a Marca.");
  if (!input.product || !input.product.trim()) errors.push("Informe o Produto.");
  if (!input.lot || !input.lot.trim()) errors.push("Informe o Lote.");
  if (input.validity && !/^\d{4}-\d{2}-\d{2}$/.test(input.validity)) {
    errors.push("A Validade deve ser uma data (aaaa-mm-dd).");
  }
  return errors;
}

/** "Sherwin DUBL-CHEK — D-100 (Lote 24A03)" */
export function formatLpConsumableLabel(item: LpConsumable): string {
  const lot = item.lot ? ` (Lote ${item.lot})` : "";
  return `${item.brand} — ${item.product}${lot}`;
}

/**
 * Validade vencida? Comparação civil: "2026-09-30" vence no dia seguinte a
 * 30/09, em qualquer fuso. Sem validade informada não se afirma vencimento.
 */
export function isLpConsumableExpired(validity: string | null | undefined, today: string = todayIso()): boolean {
  const value = (validity ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return value < today;
}

export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * O que o relatório pode oferecer num select: os ATIVOS daquele tipo, mais o
 * que o relatório já aponta — reabrir um relatório antigo nunca perde nem
 * reescreve o consumível com que foi emitido, mesmo depois de inativado.
 */
export function selectableLpConsumables(
  items: readonly LpConsumable[],
  type: LpConsumableType,
  currentlyLinkedId?: string | null
): LpConsumable[] {
  return items
    .filter(item => item.type === type)
    .filter(item => item.status === "ACTIVE" || (!!currentlyLinkedId && item.id === currentlyLinkedId))
    .sort((a, b) => formatLpConsumableLabel(a).localeCompare(formatLpConsumableLabel(b), "pt-BR", { numeric: true }));
}

/** dd/mm/aaaa a partir de "aaaa-mm-dd" — sem passar por Date(). */
export function formatCivilDateBR(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!match) return raw;
  return `${match[3]}/${match[2]}/${match[1]}`;
}
